﻿import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { Clock, DollarSign, Edit2, Plus, Trash2, Wrench } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { useCompany } from "@/lib/CompanyProvider";
import { serviceSchema, useServicesData } from "@/hooks/services/useServicesData";
import type { ServiceFormValues } from "@/hooks/services/useServicesData";
import { ServiceModal } from "@/components/services/ServiceModal";

export const Route = createFileRoute("/app/services")({
  component: ServicesPage,
});

function ServicesPage() {
  const { activeCompanyId } = useCompany();
  const [modalOpen, setModalOpen] = useState(false);

  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      name: "",
      description: "",
      price: 0,
      time_minutes: 30,
    },
  });

  const resetForm = () => form.reset();
  const closeModal = () => setModalOpen(false);

  const { services, isLoading, saveService, deleteService } = useServicesData(
    activeCompanyId,
    closeModal,
    resetForm,
  );

  const openNewModal = () => {
    form.reset({ name: "", description: "", price: 0, time_minutes: 30 });
    setModalOpen(true);
  };

  const openEditModal = (service: any) => {
    form.reset({
      id: service.id,
      name: service.name,
      description: service.description || "",
      price: Number(service.price),
      time_minutes: service.time_minutes,
    });
    setModalOpen(true);
  };

  const onSubmit = (data: ServiceFormValues) => {
    saveService.mutate(data);
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            Catálogo de Serviços
          </h1>
          <p className="text-slate-400 mt-1">
            Gerencie os serviços, valores e tempos de execução oferecidos no
            pátio.
          </p>
        </div>
        <Button
          onClick={openNewModal}
          className="bg-blue-600 hover:bg-blue-700 gap-2 font-bold"
        >
          <Plus size={18} />
          Novo Serviço
        </Button>
      </div>

      {isLoading ? (
        <div className="text-slate-500 animate-pulse">
          Carregando catálogo...
        </div>
      ) : services?.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4">
            <Wrench className="text-slate-400" size={32} />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">
            Nenhum serviço cadastrado
          </h3>
          <p className="text-slate-400 max-w-md">
            Você precisa cadastrar os serviços do seu lava-jato (como Lavagem
            Simples, Cera, etc) para poder criar Ordens de Serviço na Recepção.
          </p>
          <Button
            onClick={openNewModal}
            className="mt-6 bg-blue-600 hover:bg-blue-700"
          >
            Cadastrar Primeiro Serviço
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services?.map((svc: any) => (
            <div
              key={svc.id}
              className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition-colors group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                <button
                  onClick={() => openEditModal(svc)}
                  className="w-8 h-8 rounded bg-slate-800 text-blue-400 flex items-center justify-center hover:bg-slate-700"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => {
                    if (
                      confirm("Tem certeza que deseja excluir este serviço?")
                    ) {
                      deleteService.mutate({ id: svc.id });
                    }
                  }}
                  className="w-8 h-8 rounded bg-slate-800 text-red-400 flex items-center justify-center hover:bg-slate-700"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <h3 className="text-lg font-bold text-white mb-1 pr-16">
                {svc.name}
              </h3>
              <p className="text-sm text-slate-400 mb-4 h-10 line-clamp-2">
                {svc.description || "Sem descrição"}
              </p>

              <div className="flex items-center gap-4 pt-4 border-t border-slate-800/50 mt-auto">
                <div className="flex items-center gap-1.5 text-emerald-400 font-bold bg-emerald-400/10 px-2.5 py-1 rounded-md">
                  <DollarSign size={16} />
                  <span>{Number(svc.price).toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-1.5 text-slate-300 text-sm font-medium">
                  <Clock size={14} className="text-slate-500" />
                  <span>{svc.time_minutes} min</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ServiceModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        form={form}
        saveService={saveService}
        onSubmit={onSubmit}
      />
    </div>
  );
}
